import React, { useState, useEffect } from 'react';
import api, { API_BASE } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { DownloadSimple, TrendUp, TrendDown } from '@phosphor-icons/react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const PIE_COLORS = ['#0055FF','#22C55E','#EAB308','#EF4444','#8B5CF6','#EC4899','#06B6D4','#F97316','#6366F1','#14B8A6'];
const fmt = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0 }).format(n || 0);

export default function AnalyticsPage() {
  const [ytd, setYtd] = useState(null);
  const [vendorComp, setVendorComp] = useState([]);
  const [projected, setProjected] = useState(null);

  useEffect(() => {
    api.get('/dashboard/analytics/ytd').then(r => setYtd(r.data)).catch(console.error);
    api.get('/dashboard/analytics/vendor-comparison').then(r => setVendorComp(r.data)).catch(console.error);
    api.get('/dashboard/analytics/projected').then(r => setProjected(r.data)).catch(console.error);
  }, []);

  const monthlyData = ytd?.by_month?.map(m => ({ name: MONTHS[m.month - 1], amount: m.amount })) || [];

  const exportVendorSummary = () => {
    window.open(`${API_BASE}/api/export/vendor-summary?format=excel`, '_blank');
  };
  const exportContractExpiry = () => {
    window.open(`${API_BASE}/api/export/contract-expiry?format=excel`, '_blank');
  };

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Expense Analytics</h1>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm gap-2 text-sm" onClick={exportVendorSummary} data-testid="export-vendor-summary-btn">
            <DownloadSimple size={14} /> Vendor Summary
          </Button>
          <Button variant="outline" className="rounded-sm gap-2 text-sm" onClick={exportContractExpiry} data-testid="export-contract-expiry-btn">
            <DownloadSimple size={14} /> Contract Expiry
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="rounded-sm">
          <TabsTrigger value="overview" className="rounded-sm">Overview</TabsTrigger>
          <TabsTrigger value="vendors" className="rounded-sm">Vendor Comparison</TabsTrigger>
          <TabsTrigger value="projected" className="rounded-sm">Projected Budget</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* YTD Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="rounded-sm border md:col-span-2"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Monthly Approved Expenses (YTD)</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={monthlyData}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₱${(v/1000).toFixed(0)}k`} /><Tooltip formatter={v => fmt(v)} /><Area type="monotone" dataKey="amount" stroke="#0055FF" fill="#0055FF" fillOpacity={0.1} /></AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card className="rounded-sm border"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">By Category (YTD)</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart><Pie data={ytd?.by_category?.slice(0, 8)} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={85} label={({ category }) => category?.length > 10 ? category.slice(0,10)+'...' : category}>
                    {ytd?.by_category?.slice(0, 8).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie><Tooltip formatter={v => fmt(v)} /></PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
          <Card className="rounded-sm border"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">YTD Total: {fmt(ytd?.total)}</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="bg-[#F1F5F9]"><TableHead className="text-[10px] uppercase tracking-[0.1em]">Category</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em] text-right">Amount</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em] text-right">% of Total</TableHead></TableRow></TableHeader>
                <TableBody>
                  {ytd?.by_category?.map(c => (
                    <TableRow key={c.category}><TableCell className="text-sm">{c.category}</TableCell><TableCell className="text-right text-sm font-medium">{fmt(c.amount)}</TableCell><TableCell className="text-right text-sm text-[#475569]">{ytd?.total ? (c.amount / ytd.total * 100).toFixed(1) : 0}%</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vendors" className="space-y-4">
          <Card className="rounded-sm border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Vendor Comparison: Current Year vs Last Year</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F1F5F9]">
                    <TableHead className="text-[10px] uppercase tracking-[0.1em]">Vendor</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.1em] text-right">Current Year</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.1em] text-right">Last Year</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.1em] text-right">Difference</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.1em] text-right">Change</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendorComp.slice(0, 20).map(v => (
                    <TableRow key={v.vendor_id}>
                      <TableCell className="text-sm font-medium">{v.vendor_name}</TableCell>
                      <TableCell className="text-right text-sm">{fmt(v.current_year)}</TableCell>
                      <TableCell className="text-right text-sm text-[#475569]">{fmt(v.last_year)}</TableCell>
                      <TableCell className="text-right text-sm">{fmt(v.difference)}</TableCell>
                      <TableCell className="text-right">
                        <span className={`inline-flex items-center gap-1 text-sm font-medium ${v.change_pct >= 0 ? 'text-[#EF4444]' : 'text-[#22C55E]'}`}>
                          {v.change_pct >= 0 ? <TrendUp size={14} /> : <TrendDown size={14} />}
                          {v.change_pct > 0 ? '+' : ''}{v.change_pct}%
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="projected" className="space-y-4">
          {projected && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="rounded-sm border"><CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8]">YTD Total</p>
                  <p className="text-2xl font-bold mt-1">{fmt(projected.ytd_total)}</p>
                </CardContent></Card>
                <Card className="rounded-sm border"><CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8]">Monthly Average</p>
                  <p className="text-2xl font-bold mt-1">{fmt(projected.monthly_avg)}</p>
                </CardContent></Card>
                <Card className="rounded-sm border bg-[#0055FF]/5"><CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-[0.1em] text-[#0055FF]">Projected Annual</p>
                  <p className="text-2xl font-bold mt-1 text-[#0055FF]">{fmt(projected.projected_annual)}</p>
                  <p className="text-[10px] text-[#475569] mt-1">Based on {projected.months_elapsed} months</p>
                </CardContent></Card>
              </div>

              <Card className="rounded-sm border">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Category Projections</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader><TableRow className="bg-[#F1F5F9]"><TableHead className="text-[10px] uppercase tracking-[0.1em]">Category</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em] text-right">YTD</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em] text-right">Monthly Avg</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em] text-right">Projected Annual</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {projected.category_projections?.map(c => (
                        <TableRow key={c.category}><TableCell className="text-sm">{c.category}</TableCell><TableCell className="text-right text-sm">{fmt(c.ytd)}</TableCell><TableCell className="text-right text-sm text-[#475569]">{fmt(c.monthly_avg)}</TableCell><TableCell className="text-right text-sm font-medium">{fmt(c.projected_annual)}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <div className="p-3 rounded-sm bg-[#FEF9C3]/30 border border-[#EAB308]/20 text-sm text-[#854D0E]">
                {projected.note}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
